import { useState, type ReactNode } from "react";
import { User } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
export type AvatarShape = "circle" | "rounded" | "square";
export type AvatarStatus = "online" | "busy" | "away" | "offline";

export interface AvatarProps {
  /** Image URL */
  src?: string | null;
  /** Full name to generate initials if image is missing/broken */
  name?: string;
  /** Custom fallback text or icon if name is not available */
  fallback?: ReactNode;
  /** Size variant */
  size?: AvatarSize;
  /** Shape style */
  shape?: AvatarShape;
  /** Presence indicator */
  status?: AvatarStatus;
  alt?: string;
  className?: string;
}

const sizeClasses: Record<
  AvatarSize,
  { box: string; text: string; icon: string; dot: string; dotOffset: string }
> = {
  xs: { box: "size-6", text: "text-[10px]", icon: "size-3", dot: "size-1.5", dotOffset: "bottom-0.5 right-0.5" },
  sm: { box: "size-8", text: "text-xs", icon: "size-4", dot: "size-2", dotOffset: "bottom-0.5 right-0.5" },
  md: { box: "size-10", text: "text-body font-semibold", icon: "size-5", dot: "size-2.5", dotOffset: "bottom-1 right-1" },
  lg: { box: "size-12", text: "text-h3 font-bold", icon: "size-6", dot: "size-3", dotOffset: "bottom-1 right-1" },
  xl: { box: "size-16", text: "text-h2 font-bold", icon: "size-8", dot: "size-3.5", dotOffset: "bottom-1.5 right-1.5" },
  "2xl": { box: "size-24", text: "text-h1 font-bold", icon: "size-12", dot: "size-4", dotOffset: "bottom-2.5 right-2.5" },
};

const shapeClasses: Record<AvatarShape, string> = {
  circle: "rounded-full",
  rounded: "rounded-control",
  square: "rounded-none",
};

const statusClasses: Record<AvatarStatus, string> = {
  online: "bg-success ring-surface",
  busy: "bg-danger ring-surface",
  away: "bg-warning ring-surface",
  offline: "bg-text-muted ring-surface",
};

/** Compute 1 or 2 uppercase letters from name */
function getInitials(name?: string): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0]?.[0] || "";
    const last = parts[parts.length - 1]?.[0] || "";
    return `${first}${last}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/**
 * Avatar Component - Displays user profile pictures, employee initials, or fallback icons.
 */
export function Avatar({
  src,
  name,
  fallback,
  size = "md",
  shape = "circle",
  status,
  alt,
  className,
}: AvatarProps) {
  const [loadError, setLoadError] = useState(false);
  const s = sizeClasses[size];
  const initials = getInitials(name);
  const hasImage = Boolean(src && !loadError);

  return (
    <div
      className={cn(
        "relative inline-flex shrink-0 select-none items-center justify-center font-medium shadow-2xs border border-border/70",
        s.box,
        shapeClasses[shape],
        !hasImage && "bg-primary-soft text-primary",
        hasImage && "bg-bg overflow-hidden",
        className
      )}
      aria-label={alt || name || "Ảnh đại diện"}
    >
      {hasImage ? (
        <img
          src={src!}
          alt={alt || name || "Avatar"}
          onError={() => setLoadError(true)}
          className="size-full object-cover"
        />
      ) : fallback ? (
        fallback
      ) : initials ? (
        <span className={cn("leading-none", s.text)}>{initials}</span>
      ) : (
        <User className={cn("text-primary/70", s.icon)} />
      )}

      {/* Status Presence Dot */}
      {status && (
        <span
          className={cn(
            "absolute rounded-full ring-2",
            shape === "circle" ? s.dotOffset : "bottom-0 right-0",
            s.dot,
            statusClasses[status]
          )}
          aria-label={`Trạng thái: ${status}`}
        />
      )}
    </div>
  );
}

export interface AvatarGroupProps {
  children: ReactNode;
  max?: number;
  total?: number;
  size?: AvatarSize;
  className?: string;
}

/**
 * AvatarGroup - Stacks multiple avatars horizontally with overlap.
 */
export function AvatarGroup({
  children,
  max,
  total,
  size = "md",
  className,
}: AvatarGroupProps) {
  const s = sizeClasses[size];
  const excess = total && max ? Math.max(0, total - max) : 0;

  return (
    <div className={cn("flex items-center -space-x-2.5 overflow-hidden", className)}>
      {children}
      {excess > 0 && (
        <div
          className={cn(
            "relative inline-flex shrink-0 items-center justify-center rounded-full border-2 border-surface bg-bg text-caption font-bold text-text-secondary ring-1 ring-border shadow-2xs",
            s.box
          )}
        >
          +{excess}
        </div>
      )}
    </div>
  );
}
